import { useState, useEffect } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { assignAssetToUser, AssignToUserPayload } from 'api/assetsAPI';
import { getUsers, User } from 'api/userAPI';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface ReassignAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: {
    id: number;
    name: string;
    serial_number: string;
    current_holder_name?: string;
    current_holder_id?: number;
    company_id: number;
  } | null;
  onReassignSuccess: () => void;
}

const ReassignAssetModal = ({
  show,
  handleClose,
  asset,
  onReassignSuccess
}: ReassignAssetModalProps) => {
  const authUser = useSelector((state: RootState) => state.auth);
  const isSuperUser = authUser.role === 'super_user';

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [conditionAtAssignment, setConditionAtAssignment] = useState<
    'excellent' | 'good' | 'fair' | 'poor'
  >('good');
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load users when modal opens
  useEffect(() => {
    if (show && asset) {
      loadUsers();
    }
  }, [show, asset]);

  const loadUsers = async () => {
    if (!asset) return;

    setUsersLoading(true);
    try {
      // For superadmin, we can fetch users from the asset's company
      // For admin, we fetch from their own company (filtered by backend)
      const usersData = await getUsers(
        isSuperUser ? { company_id: asset.company_id } : undefined
      );

      // Filter out the current holder from the list
      const filteredUsers = usersData.filter(
        user => user.id !== asset.current_holder_id
      );

      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;

    if (selectedUserId === 0) {
      setError('Please select a user');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: AssignToUserPayload = {
        user_id: selectedUserId,
        condition_at_assignment: conditionAtAssignment
      };

      await assignAssetToUser(asset.id, payload);

      // Reset form
      setSelectedUserId(0);
      setConditionAtAssignment('good');

      onReassignSuccess();
      handleClose();
    } catch (err) {
      console.error('Failed to reassign asset:', err);

      // Extract error message
      const axiosError = err as {
        response?: {
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to reassign asset. Please try again.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedUserId(0);
    setConditionAtAssignment('good');
    setError(null);
    handleClose();
  };

  if (!asset) return null;

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">Reassign Device</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Asset Information */}
          <div className="mb-3 p-3 bg-body-highlight rounded">
            <h6 className="mb-2 fs-7">Device Information</h6>
            <div className="row">
              <div className="col-6">
                <small className="text-body-tertiary d-block">Asset Name</small>
                <div className="fw-semibold fs-8">{asset.name}</div>
              </div>
              <div className="col-6">
                <small className="text-body-tertiary d-block">
                  Serial Number
                </small>
                <div className="fs-8">{asset.serial_number}</div>
              </div>
              {asset.current_holder_name && (
                <div className="col-12 mt-2">
                  <small className="text-body-tertiary d-block">
                    Current Holder
                  </small>
                  <div className="fs-8 text-warning">
                    {asset.current_holder_name}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Assign To *</Form.Label>
            <Form.Select
              value={selectedUserId}
              onChange={e => setSelectedUserId(Number(e.target.value))}
              required
              disabled={usersLoading || loading}
            >
              <option value={0}>
                {usersLoading ? 'Loading users...' : 'Select a user'}
              </option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Select the new user for this device
            </Form.Text>
          </Form.Group>

          {/* Condition at Assignment */}
          <Form.Group className="mb-3">
            <Form.Label>Condition at Assignment</Form.Label>
            <Form.Select
              value={conditionAtAssignment}
              onChange={e =>
                setConditionAtAssignment(
                  e.target.value as 'excellent' | 'good' | 'fair' | 'poor'
                )
              }
              disabled={loading}
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Current condition of the device
            </Form.Text>
          </Form.Group>

          <Alert variant="info" className="mb-0">
            <i className="fas fa-info-circle me-2"></i>
            <small>
              Reassigning this device will:
              <ul className="mb-0 mt-2">
                <li>Set status to "Assigned"</li>
                <li>Update the current holder to the selected user</li>
                <li>Create a new assignment history record</li>
              </ul>
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || usersLoading || selectedUserId === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Reassigning...
              </>
            ) : (
              'Reassign Device'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReassignAssetModal;
